/**
 * Publish Listing Dialog (3-step wizard).
 *
 * Triggered ONLY for the *create* flow. Walks the seller through:
 *
 *   1. Setup    — single category (drill to leaf), title, auto-suggested
 *                  description, logo upload, source, kind/freq/interval/SLA
 *                  dropdowns, interactive tag input.
 *   2. Pricing  — Free vs Paid; Paid -> One-time / Subscription / Both.
 *   3. Publish  — required legal acks + final confirm.
 *
 * Editing an existing listing happens on the dedicated
 * ``EditListingPage`` route (``/marketplace/listings/:id/edit``) - this
 * component is intentionally create-only so the wizard can stay
 * focused on the first-time-publish UX.
 *
 * The ``existingListing`` prop is no longer accepted; passing it does
 * nothing (kept the prop for backward compatibility, but the dialog
 * always treats the open as a fresh publish).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Link as MUILink, Paper, Stack,
  Typography, alpha, useTheme,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { CMButton, CMButtonOutlined } from '../../containers/styledComponents';

import { fetchRequest as fetchCategories } from '../../redux/actions/marketplace/categories';
import {
  createListing, clearMutations,
} from '../../redux/actions/marketplace/listings';
import {
  fetchRequest as fetchSuggestionRequest,
  clearState as clearSuggestion,
} from '../../redux/actions/marketplace/listingSuggestion';

import {
  dailyFromMonthly, formatUSD, usdFromCredits, useUsdRate,
  useViewerOrganizations, useSellerStatus,
} from './marketplaceUtils';
import ListingDetailsForm from './ListingDetailsForm';
import PricingForm from './PricingForm';

const STEPS = ['Setup', 'Pricing', 'Publish'];
const TERMS_VERSION = 'v1';


const PublishListingDialog = ({
  open, onClose,
  signalKey, dataType, listingType = 'signal',
  // existingListing is intentionally ignored - edits go through the
  // EditListingPage route. Kept in the prop list so existing callers
  // don't break.
  existingListing: _existingListing,
  fetchCategories, createListing, clearMutations,
  fetchSuggestion, clearSuggestion,
  categories, mutations, suggestion,
}) => {
  const theme = useTheme();
  const usdRate = useUsdRate();
  const viewerOrgs = useViewerOrganizations();
  // Selling PUBLICLY requires the workspace's org to be a verified
  // provider (+ publisher/admin role). Without it the wizard only
  // offers the org-internal option — verification lives on the
  // Monetization page.
  const sellerStatus = useSellerStatus();
  const canPublishPublic = Boolean(sellerStatus?.can_publish);
  const [step, setStep] = useState(0);

  // ----- form state -----
  // ``visibility``: ``"public"`` (default when allowed) or ``"org"``.
  // When ``org``, pricing is forced to free at submit time and the
  // wizard hides the pricing controls.
  const initialForm = () => ({
    signal_key:            signalKey || '',
    title:                 signalKey || '',
    description:           '',
    icon_url:              '',
    source_label:          '',
    sla:                   '',
    update_frequency:      '',
    data_interval:         '',
    kind:                  'both',
    tags:                  [],
    category:              '',
    visibility:            canPublishPublic ? 'public' : 'org',
    pricing_model:         'free',
    price_monthly_credits: 0,
    price_onetime_credits: 0,
    license_acknowledged:  false,
    terms_acknowledged:    false,
  });
  const [form, setForm] = useState(initialForm);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMany = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Reset state on each open + signal change.
  useEffect(() => {
    if (!open) return;
    setStep(0);
    fetchCategories();
    setForm(initialForm());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, signalKey, fetchCategories]);

  // Suggestion is a one-shot per-open fetch keyed on signal_key. The
  // form auto-applies it on first land - no UI affordance needed.
  // Cleared on close (or missing signal) so a stale suggestion never
  // leaks into the next open.
  useEffect(() => {
    if (!open || !signalKey) { clearSuggestion(); return; }
    fetchSuggestion(signalKey);
  }, [open, signalKey, fetchSuggestion, clearSuggestion]);

  // Close + redirect on success.
  useEffect(() => {
    if (!open) return;
    if (mutations.action === 'create'
        && mutations.result && !mutations.isLoading && !mutations.error) {
      clearMutations();
      onClose && onClose(mutations.result);
    }
  }, [open, mutations, onClose, clearMutations]);

  // ----- per-step "can advance" check -----
  const canAdvance = useMemo(() => {
    if (step === 0) {
      // Need a category, a title, and either non-empty description
      // (the auto-fill will populate it from the suggestion).
      if (!form.category) return false;
      if (!form.title || !form.title.trim()) return false;
      return true;
    }
    if (step === 1) {
      // Pricing must be coherent: any negative values are invalid.
      const m = Number(form.price_monthly_credits) || 0;
      const o = Number(form.price_onetime_credits) || 0;
      if (m < 0 || o < 0) return false;
      return true;
    }
    if (step === 2) {
      return form.license_acknowledged && form.terms_acknowledged;
    }
    return false;
  }, [step, form]);

  const handleSubmit = () => {
    // Org publishing is always free - the BE enforces this, but we
    // also zero the prices on the wire so the audit log doesn't show
    // a phantom "rejected pricing" attempt.
    const isOrg = form.visibility === 'org';
    const body = {
      signal_key:            (signalKey || form.signal_key || '').trim(),
      listing_type:          listingType || 'signal',
      title:                 form.title.trim(),
      description:           (form.description || '').trim(),
      icon_url:              (form.icon_url || '').trim() || null,
      source_label:          (form.source_label || '').trim(),
      sla:                   (form.sla || '').trim(),
      update_frequency:      (form.update_frequency || '').trim(),
      data_interval:         (form.data_interval || '').trim(),
      kind:                  form.kind || 'both',
      tags:                  form.tags || [],
      categories:            form.category ? [form.category] : [],
      visibility:            isOrg ? 'org' : 'public',
      // Explicit org_uid for multi-org users. The BE falls back to
      // single-org auto-resolve when this is omitted.
      org_uid:               isOrg ? (viewerOrgs[0]?.uid || null) : null,
      price_monthly_credits: isOrg ? 0 : (Number(form.price_monthly_credits) || 0),
      price_onetime_credits: isOrg ? 0 : (Number(form.price_onetime_credits) || 0),
      license_acknowledged:  !!form.license_acknowledged,
      terms_acknowledged:    !!form.terms_acknowledged,
      terms_version:         TERMS_VERSION,
    };
    if (dataType) body.data_type = dataType;
    createListing(body);
  };

  const renderStep = () => {
    if (step === 0) {
      return (
        <Stack spacing={2}>
          {/* Visibility selector - rendered ONLY when the user belongs
              to an organization (everyone else gets the public-only
              flow). Picking "Organization" sets pricing to free at
              submit time and skips the pricing step entirely. */}
          {viewerOrgs.length > 0 && (
            <Box>
              <Typography
                component="label"
                sx={{
                  display: 'block', mb: 0.6,
                  fontSize: 12, fontWeight: 500,
                  color: 'text.secondary', lineHeight: 1.2,
                }}
              >
                Where to publish
                <Box component="span" sx={{ color: 'error.main', ml: 0.3 }}>*</Box>
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {[
                  // Public selling is reserved for verified provider
                  // orgs — hide the tile entirely when the caller
                  // can't use it (org-internal sharing stays open).
                  ...(canPublishPublic ? [{
                    value: 'public',
                    title: 'Public marketplace',
                    sub: 'Anyone on the platform can find + subscribe.',
                  }] : []),
                  {
                    value: 'org',
                    title: `Inside ${viewerOrgs[0]?.name || 'your organization'}`,
                    sub: 'Only org members see it. Always free for them.',
                  },
                ].map((opt) => {
                  const active = form.visibility === opt.value;
                  return (
                    <Box
                      key={opt.value}
                      onClick={() => set('visibility', opt.value)}
                      sx={{
                        flex: 1,
                        p: 1.25,
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        borderWidth: active ? 2 : 1,
                        borderStyle: 'solid',
                        borderColor: active ? 'primary.main' : 'divider',
                        bgcolor: active
                          ? alpha(theme.palette.primary.main, 0.04)
                          : 'background.paper',
                        transition: 'border-color 120ms ease, background-color 120ms ease',
                        '&:hover': { borderColor: 'primary.main' },
                      }}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                        {opt.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary"
                                  sx={{ fontSize: 11, lineHeight: 1.3,
                                        display: 'block', mt: 0.25 }}>
                        {opt.sub}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
              {!canPublishPublic && (
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mt: 0.5, fontSize: 11 }}>
                  Selling on the public marketplace requires your
                  organization to be a verified provider — apply from
                  the Monetization page.
                </Typography>
              )}
            </Box>
          )}

          <ListingDetailsForm
            form={form}
            set={set}
            setMany={setMany}
            categories={categories?.categories || []}
            signalKey={signalKey}
            suggestion={suggestion}
          />
        </Stack>
      );
    }
    if (step === 1) {
      // Org listings are always free - skip the pricing UI entirely
      // and show a one-line confirmation instead. Price fields stay
      // at 0 in form state.
      if (form.visibility === 'org') {
        return (
          <Box sx={{
            p: 2, borderRadius: 1.5,
            bgcolor: alpha(theme.palette.success.main, 0.06),
            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
          }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700,
                              color: 'success.main', mb: 0.5 }}>
              Free for your organization
            </Typography>
            <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block' }}>
              Listings published inside an organization don't have
              pricing — every member of{' '}
              <strong>{viewerOrgs[0]?.name || 'your organization'}</strong>{' '}
              can subscribe to this signal at no cost — pricing and
              payouts don't apply.
            </Typography>
          </Box>
        );
      }
      return <PricingForm form={form} set={set} setMany={setMany} />;
    }
    if (step === 2) {
      const monthly = Number(form.price_monthly_credits) || 0;
      const onetime = Number(form.price_onetime_credits) || 0;
      const isFree = monthly <= 0 && onetime <= 0;
      const daily = dailyFromMonthly(monthly);
      return (
        <Stack spacing={1.5}>
          {/* ── Compact summary line ── */}
          <Paper variant="outlined"
                 sx={{ p: 1.25, borderRadius: 1.5 }}>
            <Stack spacing={0.4}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                {form.title}
              </Typography>
              <Typography variant="caption" color="text.secondary"
                          sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                {form.signal_key || signalKey}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isFree ? (
                  <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>
                    Free
                  </Box>
                ) : (
                  <>
                    {onetime > 0 && (<>{formatUSD(usdFromCredits(onetime, usdRate))} one-time</>)}
                    {onetime > 0 && monthly > 0 && ' + '}
                    {monthly > 0 && (
                      <>{formatUSD(usdFromCredits(monthly, usdRate))} / mo
                      {' '}(≈ {formatUSD(usdFromCredits(daily, usdRate))} / day)</>
                    )}
                  </>
                )}
                {form.tags?.length > 0 && (
                  <> · {form.tags.map((t) => `#${t}`).join(' ')}</>
                )}
              </Typography>
            </Stack>
          </Paper>

          {/* ── Required acks - tight, single row each ── */}
          <Box sx={{
            p: 1.25, borderRadius: 1.5,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
            bgcolor: alpha(theme.palette.warning.main, 0.04),
          }}>
            <Stack direction="row" spacing={0.75} alignItems="center"
                   sx={{ mb: 0.75 }}>
              <GavelIcon sx={{ color: 'warning.main', fontSize: 14 }} />
              <Typography variant="caption"
                          sx={{ fontWeight: 700, fontSize: 11,
                                letterSpacing: 0.4, textTransform: 'uppercase',
                                color: 'warning.main' }}>
                Required acknowledgements
              </Typography>
            </Stack>
            <Stack spacing={0}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={form.license_acknowledged}
                    onChange={(e) => set('license_acknowledged', e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontSize: 12 }}>
                    I own (or have the right to redistribute) this data and
                    publishing does not violate any third-party license.
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={form.terms_acknowledged}
                    onChange={(e) => set('terms_acknowledged', e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontSize: 12 }}>
                    I agree to the{' '}
                    <MUILink href="https://predict.ai/legal/terms" target="_blank" rel="noopener" underline="hover">seller terms</MUILink>,
                    including my organization's revenue-share agreement.
                  </Typography>
                }
              />
            </Stack>
          </Box>

          {mutations.error && mutations.action === 'create' && (
            <Alert severity="error" sx={{ py: 0.5 }}>
              {String(mutations.error)}
            </Alert>
          )}
        </Stack>
      );
    }
    return null;
  };

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth
            scroll="body"
            PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        py: 1.5, fontSize: 15, fontWeight: 700,
      }}>
        <CheckCircleOutlineIcon color="primary" sx={{ fontSize: 18 }} />
        Publish to marketplace
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary"
                    sx={{ fontWeight: 600 }}>
          Step {step + 1} of {STEPS.length} · {STEPS[step]}
        </Typography>
      </DialogTitle>
      {/* Slim progress bar instead of the full Stepper - the title
          line already says which step we're on. */}
      <Box sx={{
        height: 3,
        bgcolor: alpha(theme.palette.text.primary, 0.06),
      }}>
        <Box sx={{
          height: '100%',
          width: `${((step + 1) / STEPS.length) * 100}%`,
          bgcolor: 'primary.main',
          transition: 'width 200ms ease',
        }} />
      </Box>
      <DialogContent dividers sx={{ px: 4, py: 3.5 }}>
        {renderStep()}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button size="small"
                onClick={onClose} disabled={mutations.isLoading}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        {step > 0 && (
          <CMButtonOutlined
            size="small"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={mutations.isLoading}
          >
            Back
          </CMButtonOutlined>
        )}
        {step < STEPS.length - 1 ? (
          <CMButton
            size="small"
            onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
            disabled={!canAdvance}
          >
            Next
          </CMButton>
        ) : (
          <CMButton
            size="small"
            onClick={handleSubmit}
            disabled={!canAdvance || (mutations.isLoading && mutations.action === 'create')}
            startIcon={mutations.isLoading && mutations.action === 'create'
              ? <CircularProgress size={12} />
              : null}
          >
            Publish
          </CMButton>
        )}
      </DialogActions>
    </Dialog>
  );
};


const mapStateToProps = (state) => ({
  categories: state.marketplace_categories.response,
  mutations:  state.marketplace_mutations,
  suggestion: state.marketplace_listing_suggestion.response,
});

const mapDispatchToProps = (dispatch) => ({
  fetchCategories: () => dispatch(fetchCategories()),
  createListing:   (body) => dispatch(createListing(body)),
  clearMutations:  () => dispatch(clearMutations()),
  fetchSuggestion: (signalKey) => dispatch(fetchSuggestionRequest({ signalKey })),
  clearSuggestion: () => dispatch(clearSuggestion()),
});

export default connect(mapStateToProps, mapDispatchToProps)(PublishListingDialog);
